import { useAppDispatch, useAppSelector } from "hooks/redux";
import { cloneDeep } from "lodash";
import { IWebsiteBuilderLogoSettings, TWebsiteBuilderLogoObjectFit } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderLogo";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { IWebsiteBuilderSelectOption, renderWebsiteBuilderSelectOptions } from "../../../../../helpers/renderWebsiteBuilderSelectOptions";
import { WebsiteBuilderPanelContent } from "pages/secure/facility/websiteBuilder/editor/shell/panel/content/WebsiteBuilderPanelContent";
import { websiteBuilderUpdateElementSettings } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { useWebsiteBuilderDebouncedUpdate, WebsiteBuilderDebouncedUpdateTime } from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import Callout from "components/callout/Callout";
import { Select } from "components/select/index";
import Input from "components/form/input/index";
import { WEBSITE_BUILDER_PORTAL } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";

interface ILogoSettingsProps {
  id: string;
  settings: IWebsiteBuilderLogoSettings;
  closeSettings: () => void;
}

export function LogoSettings(props: ILogoSettingsProps) {
  const { id, settings, closeSettings } = props;

  const { t } = useTranslation();

  const history = useHistory();
  
  const dispatch = useAppDispatch();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  const logoUrl = useAppSelector(store => store.websiteBuilderStore.config.website?.logo_url);
  
  const [updatedSettings, setUpdatedSettings] = useState<IWebsiteBuilderLogoSettings>(() => cloneDeep(settings));
  
  const objectFitOptions: IWebsiteBuilderSelectOption<TWebsiteBuilderLogoObjectFit>[] = useMemo(() => {
    return [
      {
        label: t("Contain"),
        value: "contain",
      },
      {
        label: t("Cover"),
        value: "cover",
      },
      {
        label: t("Fill"),
        value: "fill",
      },
    ];
  }, []);

  function dispatchSettings(prevSettings: IWebsiteBuilderLogoSettings, nextSettings: IWebsiteBuilderLogoSettings) {
    dispatch(
      websiteBuilderUpdateElementSettings(activePageId, deviceView, id, {
        id,
        prevElementSettings: {
          type: "logo",
          settings: cloneDeep(prevSettings),
        },
        nextElementSettings: {
          type: "logo",
          settings: cloneDeep(nextSettings),
        },
      }),
    );
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: settings,
    updatedState: updatedSettings,
    setUpdatedState: setUpdatedSettings,
    dispatchState: dispatchSettings,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  function renderSettings() {
    return (
      <>
        {logoUrl == null && (
          <Callout
            type="info"
            title={t("No logo uploaded")}
            actions={{
              primary: {
                label: t("Upload logo"),
                onClick: () => history.push("/admin/website-builder-logo-and-brand"),
                buttonType: "tertiary",
              },
            }}
          />
        )}
        <Select
          label={t("Fit")}
          onChange={(objectFit: TWebsiteBuilderLogoObjectFit) =>
            handleUpdateState(prev => ({
              ...prev,
              objectFit,
            }))
          }
          defaultValue={updatedSettings.objectFit}
          usePortal
          portalId={WEBSITE_BUILDER_PORTAL}
        >
          {renderWebsiteBuilderSelectOptions(objectFitOptions)}
        </Select>
        <Input
          label={t("Alternate Text")}
          value={updatedSettings.alt}
          onChange={e => {
            handleDebouncedUpdateState(prev => ({
              ...prev,
              alt: e.target.value,
            }));
          }}
        />
      </>
    );
  }

  return (
    <WebsiteBuilderPanelContent
      title={t("Logo settings")}
      closePanel={closeSettings}
      sections={[renderSettings()]}
    />
  );
}